'use client';
import React, { useEffect, useState } from 'react';
import { useScopedI18n } from '@locales/client';
import { useBreakpoints } from '@/shared/hooks/useBreakpoints';

import { BodyTextRegularB, HeadingBoldH7 } from '@/shared/ui/typography2';
import { useIsAuth } from '@/shared/providers/user-provider/use-user';
import { Condition } from '@/shared/ui/condition/condition';
import { ShowOnMobile, ShowOnDesktop } from '@/shared/ui/responsive';
import { useBrandTheme } from '@/shared/hooks/useBrandTheme';

import { StatusSlider } from './ui/status-slider';
import { RenderHint } from './ui/render-hint';
import { Box } from '@/shared/ui/box/Box';
import AuthButtons from '@/shared/ui/auth-buttons';

import { useLoyaltyList } from './model/hooks';
import {
  RootContainer,
  BannerContainer,
  BannerVideo,
  Content,
  InfoContainer,
  HintAndInfoDesktop,
  Wrapper,
  ContentWrapper,
  AuthButtonsBleed,
} from './styles';
import { SkeletonLoader } from '@/shared/ui/skeleton/skeleton';
import { formatTimestamp } from '@/shared/libs/format-timestamp';
import { StatusBanner } from './ui/status-banner';
import { useBannerAssets } from '@/shared/hooks/useBannerAssets';

const StatusPage = () => {
  const isAuth = useIsAuth();
  const { isPh } = useBrandTheme();

  const s = useScopedI18n('status');

  const [currentLevel, setCurrentLevel] = useState(0);
  const [needBets, setNeedBets] = useState(0);
  const [needBetsTS, setNeedBetsTS] = useState(0);
  const bannerAssets = useBannerAssets('statusBanner', currentLevel, isAuth);

  const { isError, loyaltyList, isLoading } = useLoyaltyList();
  const { isMobile } = useBreakpoints();

  useEffect(() => {
    if (!isLoading && !isError && loyaltyList && isAuth) {
      setCurrentLevel(loyaltyList.currentLevel);
      setNeedBets(loyaltyList.needBets);
      setNeedBetsTS(loyaltyList.needBetsTS);
    } else if (isError) {
      console.warn(isError);
    }
  }, [isLoading, loyaltyList, isError, isAuth]);

  const bannerVariant = isMobile ? 'mobile' : 'default';
  const bannerImageUrl = bannerAssets?.[bannerVariant];
  const bannerVideoUrl = bannerAssets?.video?.[bannerVariant];
  const hintAndInfo = (
    <>
      <Condition match={isAuth && needBets > 0}>
        <RenderHint needBets={needBets} needBetsTS={needBetsTS} />
      </Condition>
      <InfoContainer>{s('info')}</InfoContainer>
    </>
  );

  return (
    <RootContainer>
      <Wrapper>
        <Condition match={!isAuth}>
          <StatusBanner bannerAssets={bannerAssets} />
          <AuthButtonsBleed>
            <AuthButtons />
          </AuthButtonsBleed>
        </Condition>
        <Condition match={isAuth}>
          <BannerContainer>
            {isLoading ? (
              <SkeletonLoader height="100%" />
            ) : bannerVideoUrl ? (
              <BannerVideo
                src={bannerVideoUrl}
                poster={bannerImageUrl}
                autoPlay
                loop
                playsInline
                muted
              />
            ) : (
              <StatusBanner bannerAssets={bannerAssets} />
            )}
          </BannerContainer>
        </Condition>

        <ContentWrapper>
          <ShowOnDesktop>
            <HintAndInfoDesktop>{hintAndInfo}</HintAndInfoDesktop>
          </ShowOnDesktop>

          <Condition match={isAuth && !!loyaltyList?.expire}>
            <Box
              $borderRadius="8px"
              $border="2px solid #4B4B4B"
              $padding="18px 12px"
              $width={['calc(100%)', 'calc(100% - 32px)']}
              $marginTop={[0, '24px']}
              $display="flex"
              $flex="1"
              $gap="20px"
              $justifyContent={['center', 'space-between']}
              $alignItems="baseline"
            >
              <BodyTextRegularB color="#AFAFAF">
                {s('status_valid_until')}
              </BodyTextRegularB>
              <HeadingBoldH7>
                {formatTimestamp(loyaltyList?.expire)}
              </HeadingBoldH7>
            </Box>
          </Condition>

          <Content>
            <StatusSlider cardsCount={isPh ? 8 : 4} />
            <ShowOnMobile>{hintAndInfo}</ShowOnMobile>
          </Content>
        </ContentWrapper>
      </Wrapper>
    </RootContainer>
  );
};

export { StatusPage };
